import React, { useEffect, useMemo, useState } from 'react';
import { Alert } from 'react-bootstrap';

import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { GetSupportingExaminationPreviewDto } from '../../../common/spdCore/autogenerated/spdApiClient';

interface Props {
  harmfulFactorsIds?: string[] | null;
  articleDoctorsIds?: string[] | null;
  articlePsychologistsIds?: string[] | null;
}

export const HarmfulFactorsSupportingExaminationInfo: React.FC<Props> = ({
  harmfulFactorsIds,
  articleDoctorsIds,
  articlePsychologistsIds,
}) => {
  const spd = useSpdCore();
  const [preview, setPreview] =
    useState<GetSupportingExaminationPreviewDto | null>(null);

  const harmfulFactorIds = useMemo(
    () => harmfulFactorsIds || [],
    [harmfulFactorsIds],
  );
  const articleIds = useMemo(
    () => [...(articleDoctorsIds || []), ...(articlePsychologistsIds || [])],
    [articleDoctorsIds, articlePsychologistsIds],
  );

  const requestKey = useMemo(
    () => [...harmfulFactorIds, '|', ...articleIds].join(','),
    [harmfulFactorIds, articleIds],
  );

  useEffect(() => {
    if (!harmfulFactorIds.length) {
      setPreview(null);
      return;
    }
    spd.exams
      .getSupportingExaminationPreview({ harmfulFactorIds, articleIds })
      .then(setPreview)
      .catch(() => setPreview(null));
  }, [requestKey, spd.exams, articleIds, harmfulFactorIds]);

  if (!harmfulFactorIds.length || !preview?.articleWithAutoSupport) {
    return null;
  }

  const hasSynevo = (preview.synevoNames?.length ?? 0) > 0;
  const hasOther = (preview.otherNames?.length ?? 0) > 0;

  if (!hasSynevo && !hasOther) {
    return null;
  }

  return (
    <Alert variant="warning" className="mt-2">
      Z powodu czynników szkodliwych zalecane są:
      {hasSynevo && (
        <div>
          Synevo: <strong>{preview.synevoNames!.join(', ')}</strong>
        </div>
      )}
      {hasOther && (
        <div>
          Pozostałe: <strong>{preview.otherNames!.join(', ')}</strong>
        </div>
      )}
    </Alert>
  );
};
